import { cn } from '~lib/utils/style';

interface ProgressRingProps {
  /** Progress percent, 0–100. */
  percent: number;
  /** Outer diameter in pixels. Defaults to 28, suitable for a 32px button. */
  size?: number;
  /** Stroke width in pixels. */
  strokeWidth?: number;
  className?: string;
}

// Circular progress arc, suitable for laying over a 32px icon button. Stroke
// inherits `currentColor` so the caller controls the colour via Tailwind's
// `text-*` utilities — `text-primary` for the in-flight blue, etc.
function ProgressRing({
  percent,
  size = 28,
  strokeWidth = 2,
  className,
}: ProgressRingProps) {
  const clamped = Math.max(0, Math.min(100, percent));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;
  const center = size / 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
      className={cn('pointer-events-none absolute', className)}
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.18}
        strokeWidth={strokeWidth}
      />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        // Start the arc at 12 o'clock and grow clockwise.
        transform={`rotate(-90 ${center} ${center})`}
        style={{ transition: 'stroke-dashoffset 200ms linear' }}
      />
    </svg>
  );
}

export { ProgressRing };
